import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { deriveClientAccountStatus, useAuthStore, useMeQuery } from '@/entities/user';
import { type PendingGuestQuote, pendingGuestQuoteStorage } from '@/shared/lib';
import { useClaimGuestQuote } from './use-claim-guest-quote';

/**
 * 앱 셸의 견적 연결 훅(결정 G11). 앱에 한 번만 건다.
 *
 * 링크 화면 [주문하기]는 연결 대상을 localStorage 에 기억해 둔다. 그 뒤 로그인·가입·소셜 콜백·온보딩 어디에 내려앉든,
 * 계정이 ACTIVE 가 되는 순간 연결한다. 이메일 인증을 다른 탭·기기에서 해도 된다. 원래 탭의 온보딩이 계정을 다시 읽으면 이어진다.
 * - 기억은 로그인 상태가 바뀌거나 화면 경로가 바뀔 때마다 다시 읽는다. 같은 탭의 localStorage 쓰기는 알림이 없기 때문이다. 7일이 지난 기억은 저장소가 버린다.
 * - ACTIVE 판정은 users.me 로 한다. 브라우저에 저장된 status 는 로그인 때 값이고, 온보딩이 갱신하지 않는다.
 *   온보딩과 같은 users.me 캐시를 구독하므로, 온보딩이 다시 읽는 순간 여기서도 ACTIVE 가 된다.
 * - 연결 대기가 없거나 로그인 전이면 계정도 읽지 않는다. 비회원·일반 회원 화면에는 요청이 하나도 늘지 않는다.
 * - 일시 실패면 기억이 남는다. 다음 화면 이동 때 다시 읽은 새 기억으로 한 번 더 시도한다.
 */
export const usePendingGuestQuoteClaim = (): void => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { pathname } = useLocation();
  const [pending, setPending] = useState<PendingGuestQuote | null>(null);
  const me = useMeQuery({ enabled: pending !== null });
  const status =
    pending !== null && me.data !== undefined ? deriveClientAccountStatus(me.data) : null;
  const { claim } = useClaimGuestQuote();
  // claim 은 렌더마다 새로 만들어진다. effect 는 상태·기억이 바뀔 때만 돌게 두고, claim 은 latest ref 로 읽는다.
  const latestClaim = useRef(claim);
  latestClaim.current = claim;

  // biome-ignore lint/correctness/useExhaustiveDependencies: 경로가 바뀔 때마다 기억을 다시 읽는다 — 같은 탭의 localStorage 쓰기는 알림이 없다.
  useEffect(() => {
    setPending(isAuthenticated ? pendingGuestQuoteStorage.load() : null);
  }, [isAuthenticated, pathname]);

  useEffect(() => {
    if (status !== 'ACTIVE' || pending === null) return;
    void latestClaim.current(pending);
  }, [status, pending]);
};
